<template>
    <div class="campus-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && campus.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="campus"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                            <button
                                v-if="!campus.isDefault && editMode"
                                class="btn btn-info waves-effect waves-light m-1"
                                @click="setAsDefault"
                            >
                                <i class="far fa-star" />
                                Set as default
                            </button>
                        </div>

                        <item-information
                            v-if="editMode"
                            :edit-mode="editMode"
                            :sections="sections"
                            :options="options"
                            :item="campus"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required, email } from 'vuelidate/lib/validators';
import AddEditForm from '@/components/campuses/AddEditForm';
import validatePhoneFormat from '@/helpers/validatePhoneFormat';
import validateIsraelLocalPhoneFormat from '@/helpers/validateIsraelLocalPhoneFormat';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Campus'
    },

    validations: {
        campus: {
            name: {
                required
            },
            logoId: {
                required
            },
            phone: {
                required,
                isFormatValid: v =>
                    validatePhoneFormat(v) || validateIsraelLocalPhoneFormat(v)
            },
            email: {
                required,
                email
            },
            marathonManagementEmail: {
                email
            },
            address: {
                required
            },
            openingHours: {
                $each: {
                    day: {
                        required
                    },
                    open: {
                        required
                    },
                    close: {
                        required
                    }
                }
            },
            socialMediaUrls: {
                $each: {
                    text: {
                        required
                    },
                    url: {
                        required
                    },
                    icon: {
                        required
                    }
                }
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit campus' : 'Add campus',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Campuses',
                    to: '/campuses'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            campus: {},
            serverErrors: []
        };
    },

    computed: {
        sections() {
            const sections = {};

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },
        options() {
            const options = {};

            if (this.campus.isDefault) {
                options.deleteButton = false;
            }

            return options;
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.campus = await this.getCampus(this.id);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addCampus: 'campuses/store',
            deleteCampus: 'campuses/destroy',
            getCampus: 'campuses/show',
            updateCampus: 'campuses/update',
            changeDefault: 'campuses/changeDefault'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addCampus(this.campus);

                    this.$router.push(`/campuses/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateCampus({
                        id: this.id,
                        data: this.campus
                    });

                    this.campus.updatedAt = updatedAt;

                    this.$toaster('Campus updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteCampus(this.id);

                this.$router.push('/campuses');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        async setAsDefault() {
            try {
                await this.changeDefault(this.campus.id);

                this.campus.isDefault = true;

                this.$toaster('Default campus changed.');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    }
};
</script>
